/* Serboi size-choice draft. Preview on an unpublished copy before publishing. */
import { StandardEvents } from '@shopify/events';

const selector = 'variant-picker[data-serboi-size-name]';
const states = new WeakMap();
const optionLocks = new WeakMap();
const chooseMessage = 'Please choose a size';

function rootOf(element) {
  return element.closest('quick-add-dialog, featured-product-information, .shopify-section');
}
function stateOf(picker) {
  let state = states.get(picker);
  if (!state || state.productId !== picker.dataset.productId) {
    state = { productId: picker.dataset.productId, chosen: false, value: '', ready: false,
      generation: 0, prompt: null, message: chooseMessage };
    states.set(picker, state);
  }
  return state;
}
function sizeControls(picker) {
  const name = picker.dataset.serboiSizeName;
  const radios = Array.from(picker.querySelectorAll('input[type="radio"]'))
    .filter((input) => input.dataset.optionName === name);
  const select = Array.from(picker.querySelectorAll('select')).find((element) =>
    Array.from(element.options).some((option) => option.dataset.optionName === name));
  return { radios, select };
}
function selectedSize(picker) {
  const { radios, select } = sizeControls(picker);
  return select ? select.value : radios.find((input) => input.checked)?.value || '';
}
function formsFor(picker) {
  const root = rootOf(picker);
  return Array.from(root?.querySelectorAll('product-form-component') || []).filter((form) =>
    rootOf(form) === root && form.dataset.productId === picker.dataset.productId &&
    !form.closest('quick-add-component, product-card'));
}
function pickerFor(form) {
  const root = rootOf(form);
  return Array.from(root?.querySelectorAll(selector) || []).find((picker) =>
    rootOf(picker) === root && picker.dataset.productId === form.dataset.productId) || null;
}
function sync(picker) {
  if (!picker.isConnected || !customElements.get('variant-picker')) return;
  const state = stateOf(picker);
  const { radios, select } = sizeControls(picker);
  if (radios.length < 2 && !select) return;

  if (!state.chosen) {
    for (const radio of radios) {
      radio.checked = false;
      if (radio.hasAttribute('checked')) radio.removeAttribute('checked');
      if (radio.dataset.currentChecked !== 'false') radio.dataset.currentChecked = 'false';
      if (radio.dataset.previousChecked !== 'false') radio.dataset.previousChecked = 'false';
    }
    if (select) {
      let placeholder = select.querySelector('option[data-serboi-placeholder]');
      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = chooseMessage;
        placeholder.disabled = true;
        placeholder.dataset.serboiPlaceholder = 'true';
        select.prepend(placeholder);
      }
      for (const option of select.options) {
        if (option !== placeholder && option.hasAttribute('selected')) option.removeAttribute('selected');
      }
      if (!placeholder.hasAttribute('selected')) placeholder.setAttribute('selected', 'selected');
      select.value = '';
    }
  }

  // Keep a complete option combination while the initial size is unselected.
  for (const control of picker.querySelectorAll('input[type="radio"], select')) {
    if (radios.includes(control) || control === select) continue;
    if (!state.chosen) {
      if (!optionLocks.has(control)) optionLocks.set(control, control.disabled);
      if (!control.disabled) control.disabled = true;
    } else if (optionLocks.has(control)) {
      control.disabled = optionLocks.get(control);
      optionLocks.delete(control);
    }
  }

  if (!state.prompt || !picker.contains(state.prompt)) {
    state.prompt = document.createElement('p');
    state.prompt.className = 'serboi-size-choice-message';
    state.prompt.setAttribute('role', 'status');
    picker.prepend(state.prompt);
  }
  const message = state.ready ? '' : state.message;
  if (state.prompt.textContent !== message) state.prompt.textContent = message;
  if (state.prompt.hidden !== state.ready) state.prompt.hidden = state.ready;
  if (!state.ready) {
    for (const form of formsFor(picker)) {
      for (const button of form.querySelectorAll('button[type="submit"]')) {
        if (!button.disabled) button.disabled = true;
      }
      const accelerated = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      if (accelerated && !accelerated.hidden) accelerated.hidden = true;
    }
  }
}
function reset(picker) {
  const previous = states.get(picker);
  if (previous?.prompt?.isConnected) previous.prompt.remove();
  states.delete(picker);
  sync(picker);
}

document.addEventListener('serboi:size-choice-reset', (event) => {
  const picker = event.target instanceof Element ? event.target.closest(selector) : null;
  if (picker) reset(picker);
});

document.addEventListener('change', (event) => {
  if (!(event.target instanceof Element) || !event.isTrusted) return;
  const picker = event.target.closest(selector);
  if (!picker) return;
  const { radios, select } = sizeControls(picker);
  if (!radios.includes(event.target) && event.target !== select) return;
  const value = selectedSize(picker);
  if (!value) return;
  const state = stateOf(picker);
  state.chosen = true;
  state.value = value;
  state.ready = false;
  state.message = 'Checking selected size…';
  sync(picker);
}, true);

document.addEventListener(StandardEvents.productSelect, (event) => {
  const picker = event.target instanceof Element ? event.target.closest(selector) : null;
  if (!picker) return;
  const state = stateOf(picker);
  const generation = ++state.generation;
  state.ready = false;
  state.message = state.chosen ? 'Checking selected size…' : chooseMessage;
  sync(picker);
  event.promise?.then(({ detail }) => {
    if (!picker.isConnected || states.get(picker) !== state || generation !== state.generation) return;
    const sameProduct = !detail?.productId || String(detail.productId) === state.productId;
    state.ready = Boolean(sameProduct && state.chosen && selectedSize(picker) === state.value && detail?.resource?.available);
    state.message = state.chosen ? 'This option is unavailable. Please choose another size or colour.' : chooseMessage;
    sync(picker);
    if (state.ready) {
      // The theme still owns variant availability, quantity validation and cart submission.
      for (const form of formsFor(picker)) {
        for (const button of form.querySelectorAll('button[type="submit"]')) button.disabled = false;
        const accelerated = form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
        if (accelerated) accelerated.hidden = false;
      }
    }
  }).catch(() => {
    if (states.get(picker) !== state || generation !== state.generation) return;
    state.chosen = false;
    state.ready = false;
    state.message = 'Could not load that size. Please select a size again.';
    sync(picker);
  });
}, true);

function guard(event, form) {
  const quickAdd = form.closest('quick-add-component[data-serboi-requires-size="true"]');
  if (quickAdd) {
    event.preventDefault();
    event.stopImmediatePropagation();
    quickAdd.handleClick?.(event);
    return;
  }
  const picker = pickerFor(form);
  if (!picker || stateOf(picker).ready) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  sync(picker);
  const { radios, select } = sizeControls(picker);
  (select || radios[0])?.focus();
}
document.addEventListener('submit', (event) => {
  const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
  if (form) guard(event, form);
}, true);
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('button[type="submit"]');
  const form = button?.closest('product-form-component');
  if (form) guard(event, form);
}, true);

let scheduled = false;
function scheduleSync() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(() => {
    scheduled = false;
    document.querySelectorAll(selector).forEach(sync);
  });
}
new MutationObserver(scheduleSync).observe(document.documentElement, {
  childList: true, subtree: true, attributes: true,
  attributeFilter: ['disabled', 'hidden', 'data-product-id']
});
customElements.whenDefined('variant-picker').then(scheduleSync);
